import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { capitalize, noop } from 'lodash';

import { HuzzleUserAvatar } from 'components/HuzzleUserAvatar';
import Modal from 'components/Modal';
import IconButton from 'components/IconButton';
import { PostPollCreator } from 'components/discussions';
import { AutoHeightAdjustableInput } from 'components/AutoHeightAdjustableInput';
import { ButtonV3 } from 'components/ComponentV2';
import { useAuth } from 'lib/providers/AuthProvider';
import { TPollPostPayload, TPostType, TUserableType } from 'lib/models/discussion';
import { useDiscussions } from 'lib/providers/DiscussionsProvider';
import { useDiscussion } from 'lib/providers/DiscussionProvider';

import styles from './AddPollModal.module.scss';

type AddPollModalProps = {
  open: boolean;
  placeholder: string;
  onClose: (meta?: { postCreated: boolean }) => void;
};

const AddPollModal: React.FC<AddPollModalProps> = ({ open, placeholder, onClose }) => {
  const [t] = useTranslation('discussions');
  const { user, basicParticipant } = useAuth();
  const { addPost, societyDiscussionsSettings } = useDiscussions();
  const [pollContent, setPollContent] = useState('');
  const inputRef = useRef<HTMLDivElement>(null);
  const [postPollOptions, setPostPollOptions] = useState<TPollPostPayload['poll_answer_options_attributes']>([]);
  const [isPostSubmitting, setPostSubmittings] = useState(false);
  const { post: selectedPost } = useDiscussion();

  const handleAddOption = useCallback(() => {
    setPostPollOptions((prev) => [...prev, { value: '' }]);
  }, []);

  const updatePollOption = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, pollIndex: number) => {
      setPostPollOptions((prev) => {
        const updatedOptions = [...prev];
        updatedOptions[pollIndex] = { value: event.target.value };
        return updatedOptions;
      });
    },
    [setPostPollOptions]
  );

  const removeOption = useCallback((pollIndex: number) => {
    setPostPollOptions((prev) => {
      const updatedOptions = [...prev];
      updatedOptions.splice(pollIndex, 1);
      return updatedOptions;
    });
  }, []);

  const canSubmit = pollContent.trim().length > 0 && postPollOptions.length > 0 && postPollOptions.every((option) => option.value.trim().length > 0);

  const onSubmit = useCallback(async () => {
    if (!user) return;

    setPostSubmittings(true);
    const postPayload = {
      content: pollContent,
      userable_type: capitalize(user.userable_type) as TUserableType,
      who_can_reply: societyDiscussionsSettings.who_can_reply_to_posts,
      type: TPostType.PollPost,
      poll_answer_options_attributes: postPollOptions,
      parent_post_id: selectedPost?.id,
    } as TPollPostPayload;

    try {
      await addPost(postPayload);
      onClose({ postCreated: true });
    } catch (error) {
      console.error('Error adding post', error);
    } finally {
      setPostSubmittings(false);
    }
  }, [user, pollContent, postPollOptions, societyDiscussionsSettings, selectedPost, addPost, onClose]);

  useEffect(() => {
    if (open) {
      setPollContent('');
      setPostPollOptions([{ value: '' }, { value: '' }]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [inputRef, open]);

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      closeOnOverlayClick={false}
      className={styles['modal-wrapper']}
      contentClassName={styles['modal-container']}
    >
      <section className={cx(styles.modal)}>
        <header className={styles.header}>
          <p className={styles['title']}>{t('discussions-in-messages.new-poll-modal.title')}</p>
          <IconButton iconName="close" className={styles.close} size="auto" onClick={() => onClose()} />
        </header>
        <main className={styles['modal-content']}>
          <HuzzleUserAvatar user={basicParticipant} />
          <div className={styles['post-content']}>
            <div className={styles['post-text']}>
              <AutoHeightAdjustableInput
                value={pollContent}
                onChange={setPollContent}
                placeholder={placeholder}
                className={styles['post-text-input']}
                ref={inputRef}
              />
            </div>
            <div className={styles['post-attachment-actions']}>
              <PostPollCreator
                className={styles['icon-button']}
                pollOptions={postPollOptions}
                initializePollOptions={noop}
                handleAddOption={handleAddOption}
                updatePollOption={updatePollOption}
                removeOption={removeOption}
              />
            </div>
          </div>
        </main>
        <footer className={styles.footer}>
          <ButtonV3 onClick={() => onClose()} color="secondary">
            {t('discussions-in-messages.new-poll-modal.cancel')}
          </ButtonV3>
          <ButtonV3 onClick={onSubmit} disabled={!canSubmit} isLoading={isPostSubmitting}>
            {selectedPost ? t('discussions-in-messages.new-poll-modal.reply') : t('discussions-in-messages.new-poll-modal.post')}
          </ButtonV3>
        </footer>
      </section>
    </Modal>
  );
};

AddPollModal.displayName = 'AddPollModal';

export default AddPollModal;
